// Canvas renderer wiring: fits the canvas to its DPR-scaled size, forwards
// pointer/wheel/pinch gestures to the wasm `Viewer`, and reframes on resize.
import { Viewer } from "../pkg/viewer.js";

// Resizing reconfigures the surface and reallocates the depth texture, so
// bursts of size changes (a window drag, the mobile dock's height animation)
// are coalesced to this interval, always ending on the final size.
const RESIZE_INTERVAL_MS = 50;

// Longest edge of a captured thumbnail, in device pixels.
const THUMBNAIL_MAX_EDGE = 256;

// JPEG quality for thumbnails: small enough to store per cart item, good enough
// at this size.
const THUMBNAIL_QUALITY = 0.8;

export class Viewer3d {
  private canvas: HTMLCanvasElement;
  private viewer: Viewer;

  // Orbit with any single pointer (mouse or touch); pinch to zoom; wheel zoom.
  private pointers = new Map<number, [number, number]>();
  private lastPinch = 0;
  private resizeTimer: ReturnType<typeof setTimeout> | null = null;

  private constructor(canvas: HTMLCanvasElement, viewer: Viewer) {
    this.canvas = canvas;
    this.viewer = viewer;
    this.wire();
  }

  static async create(canvas: HTMLCanvasElement): Promise<Viewer3d> {
    fitCanvas(canvas);
    const viewer = await Viewer.create(canvas);
    return new Viewer3d(canvas, viewer);
  }

  setMesh(vertices: Float32Array, refit: boolean) {
    this.viewer.set_mesh(vertices, refit);
    this.viewer.render();
  }

  // A downscaled JPEG of the current view, or null where a 2D canvas is
  // unavailable.
  //
  // The WebGL drawing buffer is cleared as soon as the browser composites it,
  // which cannot happen before this task ends, so the frame is drawn and copied
  // out here in one uninterrupted run: everything up to the drawImage must stay
  // synchronous (hence no `async` on this method — an await before the copy
  // would read a cleared, blank buffer).
  captureThumbnail(): Promise<Blob | null> {
    this.viewer.render();

    const { width, height } = this.canvas;
    const scale = Math.min(1, THUMBNAIL_MAX_EDGE / Math.max(width, height));
    const thumbnail = document.createElement("canvas");
    thumbnail.width = Math.max(1, Math.round(width * scale));
    thumbnail.height = Math.max(1, Math.round(height * scale));
    const ctx = thumbnail.getContext("2d");
    if (!ctx) return Promise.resolve(null);
    // A thumbnail is a large downscale, so it is worth the better filter.
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(this.canvas, 0, 0, thumbnail.width, thumbnail.height);

    return new Promise((resolve) =>
      thumbnail.toBlob(resolve, "image/jpeg", THUMBNAIL_QUALITY),
    );
  }

  private wire() {
    const { canvas, viewer } = this;

    // A hidden route collapses the canvas to nothing; reconfiguring the surface
    // for that would only have to be undone on the way back.
    const hasSize = () => canvas.clientWidth > 0 && canvas.clientHeight > 0;

    new ResizeObserver(() => {
      if (!hasSize()) return;
      if (this.resizeTimer !== null) return;
      this.resizeTimer = setTimeout(() => {
        // Clearing first is safe because the canvas is sized in CSS: the new
        // backing store can't change the observed box and re-arm the timer.
        this.resizeTimer = null;
        // The route can have hidden the canvas since the timer was armed.
        if (!hasSize()) return;
        fitCanvas(canvas);
        viewer.resize(canvas.width, canvas.height);
        viewer.render();
      }, RESIZE_INTERVAL_MS);
    }).observe(canvas);

    canvas.addEventListener("pointerdown", (e) => {
      canvas.setPointerCapture(e.pointerId);
      this.pointers.set(e.pointerId, [e.clientX, e.clientY]);
      if (this.pointers.size === 2) this.lastPinch = this.pinchDistance();
    });

    canvas.addEventListener("pointermove", (e) => {
      const prev = this.pointers.get(e.pointerId);
      if (!prev) return;
      this.pointers.set(e.pointerId, [e.clientX, e.clientY]);
      if (this.pointers.size === 1) {
        viewer.orbit(e.clientX - prev[0], e.clientY - prev[1]);
        viewer.render();
      } else if (this.pointers.size === 2) {
        const pinch = this.pinchDistance();
        if (this.lastPinch > 0 && pinch > 0) {
          viewer.zoom(this.lastPinch / pinch);
          viewer.render();
        }
        this.lastPinch = pinch;
      }
    });

    for (const type of ["pointerup", "pointercancel"] as const) {
      canvas.addEventListener(type, (e) => this.pointers.delete(e.pointerId));
    }

    canvas.addEventListener(
      "wheel",
      (e) => {
        e.preventDefault();
        viewer.zoom(Math.exp(e.deltaY * 0.001));
        viewer.render();
      },
      { passive: false },
    );
  }

  private pinchDistance(): number {
    const [a, b] = [...this.pointers.values()];
    return Math.hypot(a[0] - b[0], a[1] - b[1]);
  }
}

function fitCanvas(canvas: HTMLCanvasElement) {
  const dpr = window.devicePixelRatio || 1;
  canvas.width = Math.max(1, Math.round(canvas.clientWidth * dpr));
  canvas.height = Math.max(1, Math.round(canvas.clientHeight * dpr));
}
